import { useState, useRef, useEffect, useCallback } from 'react';
import type { StorefrontProducto } from '@almacenes/core';
import type { ItemCarrito } from '../types';

function leerCarritoSession(almacenId: number, productosValidos: Set<number>): ItemCarrito[] {
  try {
    const raw = sessionStorage.getItem(`carrito-${almacenId}`);
    if (!raw) return [];
    return (JSON.parse(raw) as ItemCarrito[]).filter((i) =>
      productosValidos.has(i.producto.productoAlmacenId)
    );
  } catch {
    return [];
  }
}

function guardarCarritoSession(almacenId: number, carrito: ItemCarrito[]) {
  try {
    sessionStorage.setItem(`carrito-${almacenId}`, JSON.stringify(carrito));
  } catch {
    // sessionStorage puede estar lleno o bloqueado
  }
}

export function useCarrito(almacenId: number, productos: StorefrontProducto[] | undefined) {
  const [carrito, setCarritoRaw] = useState<ItemCarrito[]>([]);
  const inicializadoRef = useRef(false);
  const [badgePulse, setBadgePulse] = useState(false);

  useEffect(() => {
    if (productos && !inicializadoRef.current) {
      inicializadoRef.current = true;
      const validos = new Set(productos.map((p) => p.productoAlmacenId));
      const guardado = leerCarritoSession(almacenId, validos);
      if (guardado.length > 0) setCarritoRaw(guardado);
    }
  }, [productos, almacenId]);

  const setCarrito = useCallback(
    (updater: ItemCarrito[] | ((prev: ItemCarrito[]) => ItemCarrito[])) => {
      setCarritoRaw((prev) => {
        const next = typeof updater === 'function' ? updater(prev) : updater;
        guardarCarritoSession(almacenId, next);
        return next;
      });
    },
    [almacenId]
  );

  const dispararPulse = useCallback(() => {
    setBadgePulse(true);
    setTimeout(() => setBadgePulse(false), 600);
  }, []);

  const agregarAlCarrito = useCallback(
    (producto: StorefrontProducto, cantidad: number, subtotal: number) => {
      setCarrito((prev) => {
        const existe = prev.find((i) => i.producto.productoAlmacenId === producto.productoAlmacenId);
        if (existe) {
          const raw = Math.round((existe.cantidad + cantidad) * 1000) / 1000;
          const maxStock =
            producto.venderPor === 'U' && producto.stock != null ? producto.stock : Infinity;
          const nuevaCantidad = Math.min(raw, maxStock);
          return prev.map((i) =>
            i.producto.productoAlmacenId === producto.productoAlmacenId
              ? { ...i, cantidad: nuevaCantidad, subtotal: Math.round(nuevaCantidad * producto.precioVenta) }
              : i
          );
        }
        return [...prev, { producto, cantidad, subtotal }];
      });
      dispararPulse();
    },
    [setCarrito, dispararPulse]
  );

  const cambiarCantidad = useCallback(
    (productoAlmacenId: number, delta: number) => {
      setCarrito((prev) =>
        prev
          .map((i) => {
            if (i.producto.productoAlmacenId !== productoAlmacenId) return i;
            const raw = Math.round((i.cantidad + delta) * 1000) / 1000;
            const maxStock =
              i.producto.venderPor === 'U' && i.producto.stock != null ? i.producto.stock : Infinity;
            const nuevaCantidad = Math.min(raw, maxStock);
            return { ...i, cantidad: nuevaCantidad, subtotal: Math.round(nuevaCantidad * i.producto.precioVenta) };
          })
          .filter((i) => i.cantidad > 0)
      );
    },
    [setCarrito]
  );

  const setCantidadGranel = useCallback(
    (productoAlmacenId: number, cantidad: number, subtotal: number) => {
      setCarrito((prev) =>
        prev.map((i) =>
          i.producto.productoAlmacenId === productoAlmacenId ? { ...i, cantidad, subtotal } : i
        )
      );
    },
    [setCarrito]
  );

  const eliminarDelCarrito = useCallback(
    (productoAlmacenId: number) => {
      setCarrito((prev) => prev.filter((i) => i.producto.productoAlmacenId !== productoAlmacenId));
    },
    [setCarrito]
  );

  const cantidadEnCarrito = useCallback(
    (productoAlmacenId: number) =>
      carrito.find((i) => i.producto.productoAlmacenId === productoAlmacenId)?.cantidad ?? 0,
    [carrito]
  );

  const total = carrito.reduce((sum, i) => sum + i.subtotal, 0);
  const cantidadItems = carrito.reduce(
    (sum, i) => (i.producto.venderPor === 'F' ? sum + 1 : sum + i.cantidad),
    0
  );

  return {
    carrito,
    setCarrito,
    badgePulse,
    total,
    cantidadItems,
    agregarAlCarrito,
    cambiarCantidad,
    setCantidadGranel,
    eliminarDelCarrito,
    cantidadEnCarrito,
  };
}
